import React, { useEffect, useState } from "react";
import { alpha } from "@mui/material/styles";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CloseIcon from "@mui/icons-material/Close";

/**
 * Pré-visualização do que será enviado ao cliente.
 *
 * Mostra exatamente como o cliente vai receber (texto em balão de mensagem ou
 * imagem) antes de qualquer envio. Ao confirmar, o conteúdo é entregue ao
 * compositor do chat (não envia sozinho) para o atendente revisar/editar/enviar.
 *
 * `action`: { kind: "text"|"media", getText?(), getFile?(), caption?, previewTitle?, helpText? }
 */
export default function BPeSendPreviewDialog({ open, action, onClose, onConfirm, t }) {
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [file, setFile] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open || !action) return undefined;
    let cancelled = false;
    let createdUrl = null;
    setLoading(true);
    setText("");
    setImageUrl("");
    setFile(null);
    setCopied(false);

    (async () => {
      try {
        if (action.kind === "media" && action.getFile) {
          const f = await action.getFile();
          if (cancelled) return;
          if (f) {
            createdUrl = URL.createObjectURL(f);
            setFile(f);
            setImageUrl(createdUrl);
          }
        } else if (action.getText) {
          const txt = await action.getText();
          if (cancelled) return;
          setText(String(txt || ""));
        }
      } catch (_) {
        /* ignore */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [open, action]);

  if (!action) return null;

  const isMedia = action.kind === "media";
  const caption = action.caption || "";

  const handleCopy = async () => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (_) {
      /* ignore */
    }
  };

  const handleSend = () => {
    onConfirm?.({ kind: action.kind, text, file, caption });
  };

  const sendDisabled = loading || (isMedia ? !file : !text.trim());

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ py: 1.25, px: 2 }}>
        <Typography variant="h6" component="span" fontWeight={700}>
          {action.previewTitle || t("bpeWizard.assist.previewTitle")}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          {t("bpeWizard.assist.previewSubtitle")}
        </Typography>
      </DialogTitle>
      <DialogContent dividers sx={{ px: 2, py: 1.5 }}>
        {loading ? (
          <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ py: 4 }}>
            <CircularProgress size={26} />
            <Typography variant="caption" color="text.secondary">
              {t("bpeWizard.assist.previewLoading")}
            </Typography>
          </Stack>
        ) : (
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              {t("bpeWizard.assist.previewClientReceives")}
            </Typography>
            {/* Balão de mensagem imitando como o cliente vê no chat. */}
            <Box
              sx={(theme) => ({
                borderRadius: 2,
                p: 1.25,
                bgcolor: alpha(theme.palette.success.main, 0.08),
                border: `1px solid ${alpha(theme.palette.success.main, 0.25)}`,
                maxWidth: "100%",
              })}
            >
              {isMedia ? (
                <Stack spacing={1}>
                  {imageUrl ? (
                    <Box
                      component="img"
                      src={imageUrl}
                      alt={caption || "preview"}
                      sx={{ maxWidth: "100%", borderRadius: 1.5, display: "block" }}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {t("bpeWizard.assist.previewNoImage")}
                    </Typography>
                  )}
                  {caption && (
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                      {caption}
                    </Typography>
                  )}
                </Stack>
              ) : (
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                  {text || t("bpeWizard.assist.previewEmpty")}
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1, gap: 1, flexWrap: "wrap" }}>
        <Button onClick={onClose} startIcon={<CloseIcon />} sx={{ mr: "auto", textTransform: "none" }}>
          {t("bpeWizard.cancel")}
        </Button>
        {!isMedia && (
          <Button
            onClick={handleCopy}
            startIcon={<ContentCopyIcon />}
            variant="outlined"
            disabled={loading || !text.trim()}
            color={copied ? "success" : "primary"}
            sx={{ textTransform: "none" }}
          >
            {copied ? t("bpeWizard.assist.sent") : t("bpeWizard.assist.previewCopy")}
          </Button>
        )}
        <Button
          onClick={handleSend}
          startIcon={<SendIcon />}
          variant="contained"
          disabled={sendDisabled}
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          {t("bpeWizard.assist.previewSend")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
